import type { BuildUnfilledWays, UnfilledWay } from "./types";

/** The same rule the schematic lookup uses: "-0F5", "0F5" and "0f5" are one tag. */
const normalize = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * The schedule's own rows that became no run, ordered the way he reads them.
 *
 * It refuses rather than guesses, which is the precedence at the top of
 * KARL.md: a way is offered only when the schedule NAMED it and nothing on this
 * plan already carries that id. The tag being absent from the sheet is not a
 * reason to hide it — a breaker whose devices were never annotated is exactly
 * the one he has to create — so `tagOnPlan` is published and never filtered on.
 */
export const buildUnfilledWays: BuildUnfilledWays = ({ steps, takenKeys }) => {
  const taken = new Set(takenKeys.map(normalize).filter(Boolean));
  const onPlan = new Set(steps.tagsOnPlan.map(normalize).filter(Boolean));
  const seen = new Set<string>();
  const ways: UnfilledWay[] = [];

  for (const way of steps.ways) {
    if (way.circuitIds.length) continue;
    const key = way.key.trim() || way.breaker.trim();
    if (!key) continue;
    const id = normalize(key);
    if (taken.has(id) || seen.has(id)) continue;
    seen.add(id);
    ways.push({
      key,
      breaker: way.breaker.trim(),
      board: way.board,
      description: way.description,
      cableType: way.cableType,
      // Every identifier the row carries is asked, not just the one column we
      // lead with: a schedule is not consistent about which one the plan prints.
      tagOnPlan: onPlan.size
        ? [key, way.breaker, ...way.aliases].some(
            (alias) => alias && onPlan.has(normalize(alias)),
          )
        : null,
    });
  }
  return ways;
};
